import { parseQueryParams } from "../../Engine/utils";
import { FormSettings } from "../Types/FormSettings";
import { trimLastSlash } from "../../../../Common/TypingUtils";
import { FeatureFlags } from "../Types/FeatureFlags";

interface IPrepareSettingsAndFeatureFlags {
    settings: FormSettings;
    queryFeatureFlags: FeatureFlags;
}

declare const global: Window;

export function prepareSettingsAndFeatureFlags(defaultSettings: FormSettings): IPrepareSettingsAndFeatureFlags {
    const { queryParams, featureFlags } = parseQueryParams();
    const settings: FormSettings = {
        abonId: "00000000-0000-0000-0000-000000000000",
        orgId: "00000000-0000-0000-0000-000000000000",
        userId: "00000000-0000-0000-0000-000000000000",
        ...defaultSettings,
        keformsUrl: trimLastSlash(global.CF.keformsUrl ?? defaultSettings.keformsUrl),
        staffServiceUrl: trimLastSlash(global.CF.staffServiceUrl),
        picklistUrl: trimLastSlash(global.CF.picklistUrl),
        nidusUrl: trimLastSlash(global.CF.nidusUrl),
        fsPrinterUrl: trimLastSlash(global.CF.fsPrinterUrl),
        contractorsApiUrl: trimLastSlash(global.CF.contractorsApiUrl),
        ...queryParams,
        useServerSideFLangNormalization: defaultSettings.useServerSideFLangNormalization,
    };
    const normalizedSettings = normalizeSettings(settings);
    return { settings: normalizedSettings, queryFeatureFlags: featureFlags };
}

const normalizeSettings = (settings: FormSettings): FormSettings => ({
    ...settings,
    isIndividual: parseBool(settings.isIndividual),
    isForeign: parseBool(settings.isForeign),
    isTestServer: parseBool(settings.isTestServer),
    renderCachingForIE8: parseBool(settings.renderCachingForIE8),
    normalizationOnServer: parseBool(settings.normalizationOnServer),
    debug: parseBool(settings.debug),
    allowsend: parseBool(settings.allowsend),
    isDisabledCleanForm: parseBool(settings.isDisabledCleanForm),
    isDisabledCloseButton: parseBool(settings.isDisabledCloseButton),
    isDisabledCloseAndSaveButton: parseBool(settings.isDisabledCloseAndSaveButton),
    restoreToSourceFuf: parseBool(settings.restoreToSourceFuf),
    embeddedLogEnabled: parseBool(settings.embeddedLogEnabled),
    printFromEditor: parseBool(settings.printFromEditor),
    interdocumentTransfer: parseBool(settings.interdocumentTransfer),
    isReadOnly: parseBool(settings.isReadOnly),
    hideFufDownloadButton: parseBool(settings.hideFufDownloadButton),
    canCheckByPopd: parseBool(settings.canCheckByPopd),
    useExtendedErrorLog: parseBool(settings.useExtendedErrorLog),
    zeroReportingEnabled: parseBool(settings.zeroReportingEnabled),
    callInitZeroReport: parseBool(settings.callInitZeroReport),
    useServerSideFLangNormalization: parseBool(settings.useServerSideFLangNormalization),
    useHeavyErrorLog: parseBool(settings.useHeavyErrorLog),
    innerHistoryEnabled: parseBool(settings.innerHistoryEnabled),
    isFormInFormNds: parseBool(settings.isFormInFormNds),
    isNeedNdsRevise: parseBool(settings.isNeedNdsRevise),
    disableMinitours: parseBool(settings.disableMinitours),
    showErrorsCountInPanel: parseBool(settings.showErrorsCountInPanel),
    hidePanelActions: parseBool(settings.hidePanelActions),

    //Выпилить при https://yt.skbkontur.ru/issue/FS-7999/Pereehat-na-generaciyu-kontenta-futera-v-SimpleForm-iz-resursov
    sendToSignButton: {
        visible: parseBool(settings.sendToSignButton?.visible),
        title: settings.sendToSignButton?.title,
    },
});

export function parseBool(value: unknown): boolean {
    if (typeof value === "string") {
        if (value === "false") {
            return false;
        }
        if (value === "true") {
            return true;
        }
        return false;
    }
    return Boolean(value);
}

let isReadOnlyViaQueryStringCache: boolean | null = null;
export function isReadOnlyViaQueryString(): boolean {
    if (isReadOnlyViaQueryStringCache !== null) {
        return isReadOnlyViaQueryStringCache;
    }

    if (typeof window === "undefined") {
        isReadOnlyViaQueryStringCache = false;
        return isReadOnlyViaQueryStringCache;
    }

    const urlParams = new URLSearchParams(window.location.search);
    isReadOnlyViaQueryStringCache = String(urlParams.get("isReadOnly")).toLowerCase() === "true";
    return isReadOnlyViaQueryStringCache;
}
